import React from 'react';
import { MessageSquare, Send, Clock, CheckCircle, XCircle, AlertTriangle, Book, Calendar, DollarSign, PiggyBank, Plane, Snowflake, CreditCard, Wallet } from 'lucide-react';
import { collection, addDoc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { useCollection } from '../hooks/useCollection';
import { Link } from 'react-router-dom';

interface SupportTicket {
  id: string;
  userId: string;
  userEmail: string;
  subject: string;
  message: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  createdAt: Date;
  updatedAt: Date;
  responses: {
    id: string;
    userId: string;
    userEmail: string;
    message: string;
    createdAt: Date;
    isAdmin: boolean;
  }[];
}

const helpSections = [
  {
    icon: Calendar,
    title: "Financial Calendar",
    description: "The calendar is your central hub for managing income and expenses. Add paydays, bills, and track your daily balance. Features include:",
    features: [
      "Automatic balance calculations for each day",
      "Bill rescheduling to prevent overdrafts",
      "Support for recurring transactions",
      "Visual indicators for paid and unpaid bills",
      "Automatic and scheduled payment tracking"
    ]
  },
  {
    icon: Wallet,
    title: "Budget Buckets",
    description: "Organize your spending into categories with Budget Buckets - a digital envelope system for modern budgeting. Key features include:",
    features: [
      "Create buckets for different spending categories (groceries, fuel, dining)",
      "Track remaining balances in each category",
      "Record expenses as they happen",
      "Transfer funds between buckets when needed",
      "Monitor spending patterns by category",
      "Mobile-friendly interface for on-the-go expense tracking"
    ]
  },
  {
    icon: DollarSign,
    title: "Upcoming Paydays",
    description: "Track and manage your income with the Upcoming Paydays feature. This section helps you:",
    features: [
      "View your next several paydays at a glance",
      "See associated bills between paydays",
      "Calculate running balances until next payday",
      "Set up automatic savings percentages",
      "Track paid vs unpaid bills for each pay period"
    ]
  },
  {
    icon: PiggyBank,
    title: "Savings Tracker",
    description: "Monitor and grow your savings with dedicated tracking tools. Features include:",
    features: [
      "Track total savings progress",
      "View monthly savings averages",
      "Set and monitor savings goals",
      "Automatic savings calculations from paychecks",
      "Historical savings transaction records"
    ]
  },
  {
    icon: Plane,
    title: "Vacation Planner",
    description: "Plan and save for your vacations effectively. This tool helps you:",
    features: [
      "Create vacation savings goals",
      "Split costs across multiple paydays",
      "Track savings progress for each trip",
      "Manage multiple vacation plans",
      "Calculate daily savings requirements"
    ]
  },
  {
    icon: Snowflake,
    title: "Debt Snowball",
    description: "Accelerate your debt payoff using the snowball method. Features include:",
    features: [
      "Organize debts from smallest to largest",
      "Track balances and payment progress",
      "Automatically roll payments to next debt",
      "Add extra payments to accelerate payoff",
      "Calculate payoff dates and interest savings",
      "Manual balance updates for accuracy"
    ]
  },
  {
    icon: CreditCard,
    title: "Subscriptions Manager",
    description: "Keep track of your recurring subscriptions and memberships. This tool helps you:",
    features: [
      "Monitor monthly subscription costs",
      "Track due dates and payment status",
      "Calculate total monthly subscription expenses",
      "Set up automatic payment tracking",
      "Manage subscription renewals"
    ]
  }
];

export default function Support() {
  const { user } = useAuth();
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [selectedTicket, setSelectedTicket] = React.useState<SupportTicket | null>(null);
  const [reply, setReply] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [showHelp, setShowHelp] = React.useState(true);

  const ticketsQuery = React.useMemo(() => {
    if (!user?.uid) return null;
    return query(
      collection(db, 'supportTickets'),
      where('userId', '==', user.uid)
    );
  }, [user?.uid]);

  const { data: tickets } = useCollection<SupportTicket>(ticketsQuery);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      const ticketData = {
        userId: user.uid,
        userEmail: user.email,
        subject,
        message,
        status: 'open',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        responses: []
      };

      await addDoc(collection(db, 'supportTickets'), ticketData);
      setSubject('');
      setMessage('');
    } catch (error) {
      console.error('Error creating ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedTicket) return;

    setLoading(true);
    try {
      const response = {
        id: crypto.randomUUID(),
        userId: user.uid,
        userEmail: user.email,
        message: reply,
        createdAt: Timestamp.now(),
        isAdmin: false
      };

      const ticketRef = collection(db, 'supportTickets');
      await addDoc(ticketRef, {
        ...selectedTicket,
        responses: [...selectedTicket.responses, response],
        updatedAt: Timestamp.now()
      });

      setReply('');
      setSelectedTicket(null);
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Authentication Required</h3>
        <p className="mt-1 text-sm text-gray-500">Please sign in to access support.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <MessageSquare className="w-6 h-6 mr-2" />
            Support & Help
          </h2>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="flex items-center px-4 py-2 gradient-primary text-white rounded-lg hover:scale-105 transition-all duration-300 shadow-lg cyber-glow"
          >
            {showHelp ? (
              <>
                <MessageSquare className="w-4 h-4 mr-2" />
                View Support Tickets
              </>
            ) : (
              <>
                <Book className="w-4 h-4 mr-2" />
                View Help
              </>
            )}
          </button>
        </div>

        {/* New User Setup Banner */}
        <div className="mb-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-8 md:px-8 flex flex-col md:flex-row items-center justify-between">
            <div className="text-white mb-4 md:mb-0">
              <h3 className="text-xl font-bold mb-2">New to ClearCash?</h3>
              <p className="text-indigo-100">
                Get started by setting up your financial profile with our easy-to-use setup wizard.
              </p>
            </div>
            <div className="flex space-x-4">
              <Link
                to="/newuser"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50 transition-colors"
              >
                Start Setup
              </Link>
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-indigo-500 transition-colors"
              >
                Return to Dashboard
              </Link>
            </div>
          </div>
        </div>

        {showHelp ? (
          <div className="space-y-8">
            {helpSections.map((section) => (
              <div key={section.title} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
                  <div className="flex items-center text-white">
                    <section.icon className="w-6 h-6 mr-3" />
                    <h3 className="text-lg font-semibold">{section.title}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 mb-4">{section.description}</p>
                  <ul className="space-y-2">
                    {section.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* New Ticket Form */}
            <div className="md:col-span-2">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Support Ticket</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Subject</label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Message</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Submit Ticket
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Tickets List */}
            <div className="md:col-span-1">
              <div className="bg-white rounded-lg shadow-md">
                <div className="p-4 border-b">
                  <h3 className="text-lg font-medium text-gray-900">Your Tickets</h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {tickets?.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedTicket(ticket)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">{ticket.subject}</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          ticket.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                          ticket.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                          ticket.status === 'resolved' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 truncate">{ticket.message}</p>
                      <div className="mt-2 text-xs text-gray-400">
                        {ticket.createdAt.toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                  {(!tickets || tickets.length === 0) && (
                    <div className="p-4 text-center text-gray-500">
                      No tickets yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Selected Ticket Detail */}
        {selectedTicket && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{selectedTicket.subject}</h3>
                  <p className="text-sm text-gray-500">
                    {selectedTicket.createdAt.toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">{selectedTicket.message}</p>
                </div>

                {selectedTicket.responses.map((response) => (
                  <div
                    key={response.id}
                    className={`p-4 rounded-lg ${
                      response.isAdmin ? 'bg-blue-50 ml-4' : 'bg-gray-50 mr-4'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        {response.isAdmin ? 'Support Team' : response.userEmail}
                      </span>
                      <span className="text-xs text-gray-500">
                        {response.createdAt.toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-700">{response.message}</p>
                  </div>
                ))}

                <form onSubmit={handleReply} className="mt-4">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                  <div className="mt-3 flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Reply
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}